import { useState, useEffect } from 'react';
import { AccountsTable } from '@/modules/accounts/components';
import { useAccounts, useAccountsPagination, useAccountsFilters } from '@/hooks';
import { SearchInput } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function AccountsPage() {
  const { data, isLoading, error, isFetching } = useAccounts();
  const [{ page, limit }, setPagination] = useAccountsPagination();
  const [filters, setFilters] = useAccountsFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const accounts = data?.accounts ?? [];
  const totalPages = data?.pagination?.totalPages ?? 1;

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters({ ...filters, search: searchInput });
        setPagination({ page: 1, limit }); // Reset to first page on search
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, filters, setFilters, setPagination, limit]);

  const handleShadowModeChange = (value: 'all' | 'enabled' | 'disabled') => {
    setFilters({ ...filters, shadowMode: value });
    setPagination({ page: 1, limit }); // Reset to first page on filter change
  };

  const handleV3Change = (value: 'all' | 'enabled' | 'disabled') => {
    setFilters({ ...filters, v3: value });
    setPagination({ page: 1, limit });
  };

  return (
    <div className='flex h-full flex-col gap-6 p-4'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-base font-semibold'>Accounts</h1>
          <p className='text-xs text-muted-foreground'>Manage all accounts in the system</p>
        </div>
      </div>
      <div className='flex items-center gap-4'>
        <SearchInput
          placeholder='Search by name or ID...'
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className='w-[300px] rounded-md'
        />
        <Select value={filters.shadowMode} onValueChange={handleShadowModeChange}>
          <SelectTrigger className='w-[180px] border border-border bg-white'>
            <SelectValue placeholder='Shadow Mode' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All</SelectItem>
            <SelectItem value='enabled'>Shadow Mode Enabled</SelectItem>
            <SelectItem value='disabled'>Shadow Mode Disabled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.v3} onValueChange={handleV3Change}>
          <SelectTrigger className='w-[180px] border border-border bg-white'>
            <SelectValue placeholder='V3' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All</SelectItem>
            <SelectItem value='enabled'>V3 Enabled</SelectItem>
            <SelectItem value='disabled'>V3 Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className='flex-1'>
        {error ? (
          <div className='flex h-full items-center justify-center p-6'>
            <div className='max-w-md rounded-lg border border-destructive bg-destructive/10 p-4'>
              <h3 className='font-semibold text-destructive'>Error</h3>
              <p className='text-sm text-muted-foreground mt-1'>
                {error instanceof Error ? error.message : 'Failed to load accounts. Please try again.'}
              </p>
            </div>
          </div>
        ) : (
          <AccountsTable
            accounts={accounts}
            isLoading={isLoading || isFetching}
            page={page}
            limit={limit}
            totalPages={totalPages}
            onPageChange={(newPage) => setPagination({ page: newPage, limit })}
            onPageSizeChange={(newLimit) => setPagination({ page: 1, limit: newLimit })}
          />
        )}
      </div>
    </div>
  );
}
